import React, { useEffect, useMemo, useState } from "react";

import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import "./Proposals.css";

const FILTER_TYPES = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
  ALL: "all",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT_NAMES = MONTH_NAMES.map((month) => month.slice(0, 3));

const QUARTERS = [
  { key: "q1", months: [0, 1, 2] }, // Jan-Mar
  { key: "q2", months: [3, 4, 5] }, // Apr-Jun
  { key: "q3", months: [6, 7, 8] }, // Jul-Sep
  { key: "q4", months: [9, 10, 11] }, // Oct-Dec
];

const getCurrentQuarterKey = () => {
  const monthIndex = new Date().getMonth();
  const quarter = QUARTERS.find((item) => item.months.includes(monthIndex));
  return quarter?.key || "q1";
};

const getQuarterLabel = (quarterKey, year) => {
  const quarter = QUARTERS.find((item) => item.key === quarterKey);
  if (!quarter) return "—";

  const startMonth = MONTH_SHORT_NAMES[quarter.months[0]];
  const endMonth = MONTH_SHORT_NAMES[quarter.months[2]];
  const shortYear = String(year).slice(-2);

  return `${startMonth}-${endMonth}-${shortYear}`;
};

const Proposals = () => {
  const [proposals, setProposals] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [activeFilter, setActiveFilter] = useState(FILTER_TYPES.MONTHLY);

  const [search, setSearch] = useState("");

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const [selectedQuarter, setSelectedQuarter] = useState(
    getCurrentQuarterKey(),
  );

  const [filterCampaign, setFilterCampaign] = useState("all");
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      setError("");

      const [response, campaignResponse] = await Promise.all([
        leadsApi.getAll(),
        campaignApi.getAll(),
      ]);

      const leadList = Array.isArray(response)
        ? response
        : response?.data || [];

      const campaignList =
        campaignResponse?.campaigns ||
        campaignResponse?.data?.campaigns ||
        campaignResponse?.data ||
        campaignResponse ||
        [];

      setCampaigns(Array.isArray(campaignList) ? campaignList : []);

      const sentProposals = leadList.filter((lead) => {
        return (
          lead?.proposalSent === true ||
          lead?.status === "Proposal Sent" ||
          Boolean(lead?.proposalSentAt)
        );
      });

      sentProposals.sort((a, b) => {
        const dateA = new Date(getProposalDate(a));

        const dateB = new Date(getProposalDate(b));

        return dateB - dateA;
      });

      setProposals(sentProposals);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load proposals.",
      );
    } finally {
      setLoading(false);
    }
  };

  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);

    const proposalCampaigns = proposals
      .map((proposal) => String(proposal?.campaign || "").trim())
      .filter(Boolean);

    return [...new Set([...masterNames, ...proposalCampaigns])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [campaigns, proposals]);

  const filteredProposals = useMemo(() => {
    const today = new Date();

    const normalizedSearch = search.trim().toLowerCase();

    return proposals.filter((proposal) => {
      const proposalDate = new Date(getProposalDate(proposal));

      if (Number.isNaN(proposalDate.getTime())) {
        return false;
      }

      let matchesDate = true;

      if (activeFilter === FILTER_TYPES.WEEKLY) {
        const weekStart = getWeekStart(today);

        const weekEnd = new Date(weekStart);

        weekEnd.setDate(weekStart.getDate() + 7);

        matchesDate = proposalDate >= weekStart && proposalDate < weekEnd;
      }

      if (activeFilter === FILTER_TYPES.MONTHLY) {
        matchesDate =
          proposalDate.getFullYear() === Number(selectedYear) &&
          proposalDate.getMonth() === Number(selectedMonth);
      }

      if (activeFilter === FILTER_TYPES.QUARTERLY) {
        const quarter = QUARTERS.find((item) => item.key === selectedQuarter);

        matchesDate =
          proposalDate.getFullYear() === Number(selectedYear) &&
          Boolean(quarter) &&
          quarter.months.includes(proposalDate.getMonth());
      }

      if (activeFilter === FILTER_TYPES.YEARLY) {
        matchesDate = proposalDate.getFullYear() === Number(selectedYear);
      }

      const searchableText = [
        proposal?.company,
        proposal?.contact,
        proposal?.email,
        proposal?.proposalReference,
        proposal?.proposalNotes,
        proposal?.status,
        proposal?.campaign,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      const matchesCampaign =
        filterCampaign === "all" ||
        String(proposal?.campaign || "").trim() === filterCampaign;

      return matchesDate && matchesSearch && matchesCampaign;
    });
  }, [
    proposals,
    activeFilter,
    selectedMonth,
    selectedQuarter,
    selectedYear,
    search,
    filterCampaign,
  ]);

  const totalProposalValue = useMemo(() => {
    return filteredProposals.reduce((total, proposal) => {
      return total + Number(proposal?.proposalValue || proposal?.estValue || 0);
    }, 0);
  }, [filteredProposals]);

  const uniqueCompanies = useMemo(() => {
    return new Set(
      filteredProposals.map((proposal) => proposal?.company).filter(Boolean),
    ).size;
  }, [filteredProposals]);

  const availableYears = useMemo(() => {
    const years = proposals
      .map((proposal) => {
        const date = new Date(getProposalDate(proposal));

        return Number.isNaN(date.getTime()) ? null : date.getFullYear();
      })
      .filter(Boolean);

    years.push(new Date().getFullYear());

    return [...new Set(years)].sort((a, b) => b - a);
  }, [proposals]);

  if (loading) {
    return (
      <div className="proposal-page">
        <div className="proposal-state-card">Loading proposals...</div>
      </div>
    );
  }

  return (
    <div className="proposal-page">
      <div className="proposal-header">
        <div>
          <p className="proposal-eyebrow">CRM / Sales</p>

          <h1>Sent Proposals</h1>

          <p>
            View all proposals sent to clients weekly, monthly, quarterly and
            yearly.
          </p>
        </div>

        <button
          type="button"
          className="proposal-refresh-button"
          onClick={fetchProposals}
        >
          Refresh
        </button>
      </div>

      {error && <div className="proposal-error">{error}</div>}

      <div className="proposal-summary-grid">
        <SummaryCard label="Total Proposals" value={filteredProposals.length} />

        <SummaryCard
          label="Total Proposal Value"
          value={formatCurrency(totalProposalValue)}
        />

        <SummaryCard label="Companies" value={uniqueCompanies} />

        <SummaryCard
          label="Current View"
          value={
            filterCampaign === "all"
              ? getFilterLabel(activeFilter, selectedQuarter, selectedYear)
              : `${getFilterLabel(
                  activeFilter,
                  selectedQuarter,
                  selectedYear,
                )} • ${filterCampaign}`
          }
        />
      </div>

      <div className="proposal-toolbar">
        <div className="proposal-filter-buttons">
          <FilterButton
            label="Weekly"
            active={activeFilter === FILTER_TYPES.WEEKLY}
            onClick={() => setActiveFilter(FILTER_TYPES.WEEKLY)}
          />

          <FilterButton
            label="Monthly"
            active={activeFilter === FILTER_TYPES.MONTHLY}
            onClick={() => setActiveFilter(FILTER_TYPES.MONTHLY)}
          />

          <FilterButton
            label="Quarterly"
            active={activeFilter === FILTER_TYPES.QUARTERLY}
            onClick={() => setActiveFilter(FILTER_TYPES.QUARTERLY)}
          />

          <FilterButton
            label="Yearly"
            active={activeFilter === FILTER_TYPES.YEARLY}
            onClick={() => setActiveFilter(FILTER_TYPES.YEARLY)}
          />

          <FilterButton
            label="All"
            active={activeFilter === FILTER_TYPES.ALL}
            onClick={() => setActiveFilter(FILTER_TYPES.ALL)}
          />
        </div>

        <div className="proposal-toolbar-fields">
          {activeFilter === FILTER_TYPES.MONTHLY && (
            <select
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              className="proposal-select"
            >
              {MONTH_NAMES.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
          )}

          {activeFilter === FILTER_TYPES.QUARTERLY && (
            <select
              value={selectedQuarter}
              onChange={(event) => setSelectedQuarter(event.target.value)}
              className="proposal-select"
            >
              {QUARTERS.map((quarter) => (
                <option key={quarter.key} value={quarter.key}>
                  {getQuarterLabel(quarter.key, selectedYear)}
                </option>
              ))}
            </select>
          )}

          {(activeFilter === FILTER_TYPES.MONTHLY ||
            activeFilter === FILTER_TYPES.QUARTERLY ||
            activeFilter === FILTER_TYPES.YEARLY) && (
            <select
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              className="proposal-select"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}

          <select
            value={filterCampaign}
            onChange={(event) => setFilterCampaign(event.target.value)}
            className="proposal-select"
          >
            <option value="all">All Campaigns</option>
            {availableCampaigns.map((campaign) => (
              <option key={campaign} value={campaign}>
                {campaign}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="proposal-search"
            placeholder="Search company, contact or reference"
          />
        </div>
      </div>

      <div className="proposal-table-card">
        <div className="proposal-table-heading">
          <div>
            <h2>Proposal List</h2>

            <p>
              {filteredProposals.length} proposal
              {filteredProposals.length === 1 ? "" : "s"} found
            </p>
          </div>
        </div>

        {filteredProposals.length === 0 ? (
          <div className="proposal-empty-state">
            <div className="proposal-empty-icon">📄</div>

            <h3>No proposals found</h3>

            <p>No sent proposals match the selected filter.</p>
          </div>
        ) : (
          <div className="proposal-table-wrapper">
            <table className="proposal-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Campaign</th>
                  <th>Reference</th>
                  <th>Proposal Date</th>
                  <th>Sent Date</th>
                  <th>Value</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredProposals.map((proposal, index) => (
                  <tr key={proposal?._id || `${proposal?.company}-${index}`}>
                    <td>{index + 1}</td>

                    <td>
                      <div className="proposal-company-cell">
                        <span className="proposal-company-avatar">
                          {getInitials(proposal?.company)}
                        </span>

                        <div>
                          <strong>{proposal?.company || "—"}</strong>

                          <span>{proposal?.source || "CRM Lead"}</span>
                        </div>
                      </div>
                    </td>

                    <td>{proposal?.contact || "—"}</td>

                    <td>
                      {proposal?.email ? (
                        <a
                          href={`mailto:${proposal.email}`}
                          className="proposal-email-link"
                        >
                          {proposal.email}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>{proposal?.campaign || "—"}</td>

                    <td>{proposal?.proposalReference || "—"}</td>

                    <td>{formatDate(proposal?.proposalDate)}</td>

                    <td>
                      {formatDateTime(
                        proposal?.proposalSentAt || proposal?.updatedAt,
                      )}
                    </td>

                    <td className="proposal-value-cell">
                      {formatCurrency(
                        proposal?.proposalValue || proposal?.estValue || 0,
                      )}
                    </td>

                    <td>
                      <span className="proposal-status-badge">
                        Proposal Sent
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value }) => {
  return (
    <div className="proposal-summary-card">
      <span>{label}</span>

      <strong>{value}</strong>
    </div>
  );
};

const FilterButton = ({ label, active, onClick }) => {
  return (
    <button
      type="button"
      className={[
        "proposal-filter-button",
        active ? "proposal-filter-button-active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

const getProposalDate = (proposal) => {
  return (
    proposal?.proposalSentAt ||
    proposal?.proposalDate ||
    proposal?.updatedAt ||
    proposal?.createdAt
  );
};

const getWeekStart = (date) => {
  const result = new Date(date);

  result.setHours(0, 0, 0, 0);

  const day = result.getDay();

  const difference = day === 0 ? -6 : 1 - day;

  result.setDate(result.getDate() + difference);

  return result;
};

const getInitials = (value) => {
  const words = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return "PR";
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getFilterLabel = (filter, quarterKey, year) => {
  if (filter === FILTER_TYPES.WEEKLY) {
    return "This Week";
  }

  if (filter === FILTER_TYPES.MONTHLY) {
    return "Monthly";
  }

  if (filter === FILTER_TYPES.QUARTERLY) {
    return getQuarterLabel(quarterKey, year);
  }

  if (filter === FILTER_TYPES.YEARLY) {
    return "Yearly";
  }

  return "All Time";
};

export default Proposals;
